// A PICTURE OF PART OF THE MAP, on the clipboard.
//
// Everything the reader chose, nothing they did not: the labels are inside the
// <svg> and come with it, and the toolbar, the picker and the evidence panel
// are outside it and cannot.
//
// The hard part is that an <svg> serialized to a data: URL is loaded by the
// browser as a DOCUMENT OF ITS OWN. It cannot see this page's stylesheet, so
// every class-based rule is lost — and worse, `var(--ink)` resolves to nothing
// rather than to a colour. So the clone carries its styles as attributes,
// copied from what the browser actually computed for each live element. That
// is slower than writing a stylesheet by hand and it cannot go out of step
// with one.

// Only what a drawing needs. Copying every computed property would be a
// megabyte of markup for a picture of forty circles.
const PROPERTIES = [
  "fill", "fill-opacity", "stroke", "stroke-width", "stroke-dasharray",
  "stroke-linejoin", "stroke-linecap", "opacity", "font-family", "font-size",
  "font-weight", "letter-spacing", "text-anchor", "paint-order", "display",
  "visibility"
];

const SCALE = 2;

function inlineStyles(live, clone) {
  const computed = window.getComputedStyle(live);
  const declarations = PROPERTIES
    .map((name) => `${name}:${computed.getPropertyValue(name)}`)
    .filter((one) => !one.endsWith(":"))
    .join(";");
  clone.setAttribute("style", declarations);
  clone.removeAttribute("class");

  const liveChildren = live.children;
  const cloneChildren = clone.children;
  for (let index = 0; index < liveChildren.length; index += 1) {
    inlineStyles(liveChildren[index], cloneChildren[index]);
  }
}

// The chosen rectangle, in the SVG's own user units, so the picture is of the
// map rather than of the screen.
//
// The default preserveAspectRatio is "xMidYMid meet": the viewBox is scaled
// UNIFORMLY to fit and then centred, so unless the two aspect ratios happen to
// agree there are bars down two sides. Dividing each axis by its own ratio —
// which is what this did first — silently skewed and offset every capture
// whose map was not exactly the shape of its frame.
export function regionToViewBox(svg, rect) {
  const box = svg.getBoundingClientRect();
  const view = svg.viewBox.baseVal;

  const scale = Math.min(box.width / view.width, box.height / view.height);
  const drawnWidth = view.width * scale;
  const drawnHeight = view.height * scale;
  const left = box.left + ((box.width - drawnWidth) / 2);
  const top = box.top + ((box.height - drawnHeight) / 2);

  return {
    x: view.x + ((rect.left - left) / scale),
    y: view.y + ((rect.top - top) / scale),
    width: Math.max(rect.width / scale, 1),
    height: Math.max(rect.height / scale, 1)
  };
}

export async function captureRegion(svg, rect, { background = "#0e1116" } = {}) {
  const region = regionToViewBox(svg, rect);
  const clone = svg.cloneNode(true);
  inlineStyles(svg, clone);

  clone.setAttribute("viewBox", `${region.x} ${region.y} ${region.width} ${region.height}`);
  clone.setAttribute("width", region.width);
  clone.setAttribute("height", region.height);
  clone.removeAttribute("style");
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");

  const markup = new XMLSerializer().serializeToString(clone);
  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;

  const image = new Image();
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = () => reject(new Error("the picture could not be drawn"));
    image.src = url;
  });

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(region.width * SCALE);
  canvas.height = Math.round(region.height * SCALE);
  const context = canvas.getContext("2d");
  // The page's ground, or the picture arrives transparent and is unreadable
  // wherever it is pasted.
  context.fillStyle = background;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("the picture was empty"))), "image/png");
  });
}

// A clipboard write must begin inside the gesture that asked for it.
//
// This took a Blob and was called after `await captureRegion(...)` — which
// awaits an image load and a canvas encode. By the time it ran, the click that
// authorised it was long over, and the browser refused: the picture was
// drawn correctly and then thrown away. It takes the PROMISE now and is called
// synchronously on pointer-up, which is also the form Safari has always
// required.
export function copyToClipboard(blobPromise) {
  if (!window.isSecureContext) {
    throw new Error("the clipboard needs https or localhost");
  }
  if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") {
    throw new Error("this browser will not put an image on the clipboard");
  }
  if (typeof document !== "undefined" && document.hasFocus && !document.hasFocus()) {
    throw new Error("the page has to be focused");
  }

  return navigator.clipboard.write([new ClipboardItem({ "image/png": blobPromise })]);
}

// When the clipboard will not have it, the picture is still a picture. Better
// a file the reader has than a thing that drew correctly and vanished.
export async function downloadInstead(blobPromise, name = "correspondences.png") {
  const blob = await blobPromise;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Not immediately: Safari has not finished with the URL when click returns.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function groundColour() {
  if (typeof document === "undefined") return "#0e1116";
  const value = getComputedStyle(document.documentElement).getPropertyValue("--ground");
  return value.trim() || "#0e1116";
}
